// Lightweight, dependency-free exporters for the Accu Desk reports.
// - downloadCSV(filename, headers, rows): downloads a CSV file
// - openPrintablePDF({ title, subtitle, sections }): opens a print-ready HTML window,
//   auto-triggers window.print(), user saves as PDF via native dialog.

function csvEscape(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function downloadCSV(filename, headers, rows) {
  const lines = [headers.map(csvEscape).join(',')];
  for (const r of rows) lines.push(r.map(csvEscape).join(','));
  // Prepend UTF-8 BOM for Excel compatibility (Rand symbol, accents)
  const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * sections: Array<{ title: string, headers: string[], rows: any[][], footNote?: string }>
 */
export function openPrintablePDF({ title, subtitle, sections, firmName }) {
  const now = new Date().toLocaleString(undefined, {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const logoUrl = `${window.location.origin}/brand/accu-desk-logo.png`;

  const sectionHTML = sections.map((s) => {
    const rows = (s.rows || []).map((r) => (
      `<tr>${r.map((c, i) => `<td class="${i === 0 ? 'first' : ''}">${escapeHTML(c)}</td>`).join('')}</tr>`
    )).join('');
    const emptyRow = (s.rows || []).length === 0
      ? `<tr><td colspan="${s.headers.length}" class="empty">No data in this section.</td></tr>` : '';
    return `
      <section class="report-section">
        <h2>${escapeHTML(s.title)}</h2>
        <table>
          <thead><tr>${s.headers.map((h) => `<th>${escapeHTML(h)}</th>`).join('')}</tr></thead>
          <tbody>${rows || emptyRow}</tbody>
        </table>
        ${s.footNote ? `<div class="foot">${escapeHTML(s.footNote)}</div>` : ''}
      </section>`;
  }).join('');

  const html = `<!doctype html>
<html><head><meta charset="utf-8"/>
<title>${escapeHTML(title)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1c1917; margin: 0; padding: 32px 40px; background: #fff; }
  header.brand { display:flex; align-items:center; gap:12px; padding-bottom:18px; border-bottom:2px solid #065f46; margin-bottom:24px; }
  header.brand .logo { width:44px; height:44px; border-radius:8px; background:#000; display:grid; place-items:center; overflow:hidden; }
  header.brand .logo img { width:100%; height:100%; object-fit:contain; }
  header.brand .brand-text .name { font-weight:700; font-size:16px; color:#065f46; letter-spacing:-0.01em; }
  header.brand .brand-text .tag { font-size:10px; text-transform:uppercase; letter-spacing:0.12em; color:#78716c; margin-top:2px; }
  header.brand .meta { margin-left:auto; text-align:right; font-size:11px; color:#57534e; }
  h1 { font-family: Georgia, 'Times New Roman', serif; font-size: 28px; margin: 0 0 6px 0; color:#0c0a09; letter-spacing:-0.02em; }
  .subtitle { color:#57534e; font-size:13px; margin-bottom:28px; }
  section.report-section { margin: 0 0 28px 0; page-break-inside: avoid; }
  section.report-section h2 { font-family: Georgia, 'Times New Roman', serif; font-size:17px; margin:0 0 10px 0; color:#0c0a09;
    border-left: 3px solid #0ea5e9; padding-left:10px; }
  table { width:100%; border-collapse: collapse; font-size:12px; }
  thead th { text-align:left; background:#f5f5f4; color:#44403c; font-size:10px; text-transform:uppercase; letter-spacing:0.08em; padding:8px 10px; border-bottom:1px solid #e7e5e4; font-weight:600; }
  tbody td { padding:9px 10px; border-bottom:1px solid #f5f5f4; color:#1c1917; }
  tbody td.first { font-weight:600; color:#0c0a09; }
  tbody td.empty { text-align:center; color:#a8a29e; font-style:italic; padding:16px; }
  tbody td:not(.first) { font-variant-numeric: tabular-nums; }
  .foot { font-size:10px; color:#78716c; margin-top:6px; font-style: italic; }
  footer.page-foot { margin-top:32px; padding-top:14px; border-top:1px solid #e7e5e4; display:flex; justify-content:space-between; font-size:10px; color:#78716c; }
  @media print {
    body { padding: 20px 24px; }
    header.brand { border-bottom-color:#065f46; }
    .no-print { display:none !important; }
    section.report-section { page-break-inside: avoid; }
  }
  .print-btn { position: fixed; top: 16px; right: 16px; background:#065f46; color:#fff; border:none; padding:10px 18px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; box-shadow:0 4px 12px rgba(0,0,0,0.15); }
  .print-btn:hover { background:#064e3b; }
</style>
</head>
<body>
  <button class="print-btn no-print" onclick="window.print()">Save as PDF</button>
  <header class="brand">
    <div class="logo"><img src="${logoUrl}" alt="Accu Desk"/></div>
    <div class="brand-text">
      <div class="name">Accu Desk</div>
      <div class="tag">powered by Saber Accounting</div>
    </div>
    <div class="meta">
      ${firmName ? `<div><strong>${escapeHTML(firmName)}</strong></div>` : ''}
      <div>Generated ${escapeHTML(now)}</div>
    </div>
  </header>
  <h1>${escapeHTML(title)}</h1>
  ${subtitle ? `<div class="subtitle">${escapeHTML(subtitle)}</div>` : ''}
  ${sectionHTML}
  <footer class="page-foot">
    <div>Accu Desk · Alberton, Johannesburg · info@accudesk.co.za</div>
    <div>Confidential</div>
  </footer>
  <script>
    // Auto-open the print dialog after the logo has loaded (or after a short timeout as a fallback)
    (function() {
      var opened = false;
      function go() { if (opened) return; opened = true; setTimeout(function(){ window.print(); }, 250); }
      var img = document.querySelector('header.brand img');
      if (img && !img.complete) { img.addEventListener('load', go); img.addEventListener('error', go); }
      setTimeout(go, 900);
    })();
  </script>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) { alert('Please allow pop-ups so Accu Desk can generate the PDF.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

function escapeHTML(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
